import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** EAA-1779: which credential a reset sets. Mirrors the API's `mode` response. */
export type ResetCredentialMode = 'temp_password' | 'admin_set';

/**
 * EAA-1779: must equal `AccountResetPasswordSerializer.new_password`'s
 * `min_length` on the server. Kept as a named constant so the two are obviously
 * paired — a client floor lower than the server's turns a typo into a 400 the
 * operator only discovers after submitting.
 */
export const MIN_ADMIN_PASSWORD_LENGTH = 8;

export interface ResetCredential {
    mode: ResetCredentialMode;
    password: string;
    confirm: string;
    forceChange: boolean;
    setMode: (mode: ResetCredentialMode) => void;
    setPassword: (value: string) => void;
    setConfirm: (value: string) => void;
    setForceChange: (value: boolean) => void;
    clear: () => void;
    /** Validation message for the current input, or null when it is submittable. */
    error: string | null;
    /** False while the admin-set fields are empty, short, or mismatched. */
    valid: boolean;
    /** The credential half of a `resetAccountPassword` body. */
    toRequest: () => { new_password?: string; force_change?: boolean };
}

/**
 * EAA-1779: the credential choice shared by the single-account reset drawer and
 * the bulk reset dialog.
 *
 * It lives in a hook rather than in either screen because the two must agree on
 * the rules exactly — same minimum length, same confirm-match check, same
 * default of "permanent unless asked". Two hand-written copies is how the
 * bulk path ends up accepting a 6-character password that the single path
 * rejects.
 *
 * Note `toRequest()` omits both keys entirely in `temp_password` mode: the
 * server derives the mode from `new_password`'s PRESENCE, so sending an empty
 * string or a stray `force_change` would be describing a mode we are not in.
 */
export function useResetCredential(): ResetCredential {
    const { t } = useTranslation();
    const [mode, setMode] = useState<ResetCredentialMode>('temp_password');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [forceChange, setForceChange] = useState(false);

    const clear = useCallback(() => {
        setMode('temp_password');
        setPassword('');
        setConfirm('');
        setForceChange(false);
    }, []);

    const error = useMemo(() => {
        if (mode !== 'admin_set') return null;
        if (!password) return null; // Nothing typed yet is not yet an error.
        if (password.length < MIN_ADMIN_PASSWORD_LENGTH) {
            return t('console.accounts.resetModal.errorTooShort', {
                count: MIN_ADMIN_PASSWORD_LENGTH,
            });
        }
        if (confirm && password !== confirm) return t('console.accounts.resetModal.errorMismatch');
        return null;
    }, [mode, password, confirm, t]);

    const valid =
        mode === 'temp_password' ||
        (password.length >= MIN_ADMIN_PASSWORD_LENGTH && password === confirm);

    const toRequest = useCallback(() => {
        if (mode !== 'admin_set') return {};
        return { new_password: password, force_change: forceChange };
    }, [mode, password, forceChange]);

    return {
        mode,
        password,
        confirm,
        forceChange,
        setMode,
        setPassword,
        setConfirm,
        setForceChange,
        clear,
        error,
        valid,
        toRequest,
    };
}
